'use client';

import '@/assets/css/components/signpost.css';

import { useQuery } from '@tanstack/react-query';
import { debounce } from 'radash';
import { useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';

import { ButtonLink } from '@/components/modals/signpost/button-link';
import { fixedLinks } from '@/components/modals/signpost/signpost.config';
import { SignpostHeader } from '@/components/modals/signpost/signpost-header';
import { ClubSwiperItem } from '@/components/modals/signpost/signpost-swiper-items/club-swiper-item';
import { PlayerSwiperItem } from '@/components/modals/signpost/signpost-swiper-items/player-swiper-item';
import { DialogScrollbar } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Swiper } from '@/components/ui/swiper';
import { SwiperKeyboardAccesibility } from '@/components/ui/swiper/swiper-keyboard-accesibility';
import { useLogout } from '@/hooks/use-logout';
import { getApiV1SignpostClubs, getApiV1SignpostPlayers } from '@/query/signpost';

interface SignpostContentProps {
  setIsOpen: (isOpen: boolean) => void;
}

const swiperProps = {
  breakpoints: {
    640: {
      slidesPerView: 2,
    },
    1024: {
      slidesPerView: 4,
    },
  },
  btnLeftClassName: 'md:-translate-x-1/3',
  btnRightClassName: 'md:translate-x-1/3',
};

export const SignpostContent = ({ setIsOpen }: SignpostContentProps) => {
  const { logout } = useLogout();

  const { data: clubs = [] } = useQuery(getApiV1SignpostClubs);

  const [searchQuery, setSearchQuery] = useState('');

  const [hasPlayers, setHasPlayers] = useState(false);

  const { data: filteredPlayers = [] } = useQuery(getApiV1SignpostPlayers(searchQuery));

  const [filteredClubs, setFilteredClubs] = useState(clubs);

  useEffect(() => {
    if (searchQuery === '' && filteredPlayers.length > 0) {
      setHasPlayers(true);
    }
  }, [searchQuery, filteredPlayers]);

  const topLinks = useMemo(
    () => fixedLinks(hasPlayers, clubs.length > 0),
    [hasPlayers, clubs.length],
  );

  const handleLogout = () => {
    flushSync(() => {
      setIsOpen(false);
    });
    logout();
  };

  const handlePlayerSearch = debounce({ delay: 300 }, (query: string) => {
    setSearchQuery(query);
  });

  const handleClubSearch = (query: string) => {
    setFilteredClubs(
      clubs.filter(club =>
        club.name.toLowerCase().includes(query.toLowerCase()),
      ),
    );
  };

  useEffect(() => {
    setFilteredClubs(clubs);
  }, [clubs]);

  return (
    <DialogScrollbar className="flex w-full flex-col gap-12 px-4">
      <SignpostHeader onLogout={handleLogout} />

      <div>
        <span className="text-lg font-semibold text-blue-600 sm:text-2xl">
          Základní správa
        </span>

        <div className="mt-2 grid grid-cols-2 gap-2 md:grid-cols-4">
          {topLinks.map(btn => (
            <ButtonLink key={btn.href} {...btn} setIsOpen={setIsOpen} />
          ))}
        </div>
      </div>

      {filteredPlayers.length === 1 && filteredPlayers[0] !== undefined && (
        <div>
          <div className="mb-4 flex flex-col items-center justify-between gap-2 sm:flex-row sm:gap-x-4">
            <span className="text-lg font-semibold text-blue-600 sm:text-2xl">
              Můj profil
            </span>
          </div>

          <div className="w-1/4">
            <PlayerSwiperItem
              key={filteredPlayers[0].id}
              player={filteredPlayers[0]}
              onHide={() => setIsOpen(false)}
            />
          </div>
        </div>
      )}

      {hasPlayers && (
        <div>
          <div className="mb-4 flex flex-col items-center justify-between gap-2 sm:flex-row sm:gap-x-4">
            <span className="text-lg font-semibold text-blue-600 sm:text-2xl">
              Moji hráči
            </span>
            <Input
              className="max-w-[19rem]"
              placeholder="Hledat hráče"
              onChange={e => handlePlayerSearch(e.target.value)}
            />
          </div>

          <SwiperKeyboardAccesibility
            {...swiperProps}
            items={filteredPlayers.map(player => (
              <PlayerSwiperItem
                key={player.id}
                player={player}
                onHide={() => setIsOpen(false)}
              />
            ))}
          />
        </div>
      )}

      {clubs.length > 0 && (
        <div>
          <div className="mb-4 flex flex-col items-center justify-between gap-2 sm:flex-row sm:gap-x-4">
            <span className="text-lg font-semibold text-blue-600 sm:text-2xl">
              Moje oddíly
            </span>
            <Input
              className="max-w-[19rem]"
              placeholder="Hledat oddíl"
              onChange={e => handleClubSearch(e.target.value)}
            />
          </div>

          <Swiper
            {...swiperProps}
            items={filteredClubs.map(club => (
              <ClubSwiperItem
                key={club.id}
                club={club}
                onHide={() => setIsOpen(false)}
              />
            ))}
          />
        </div>
      )}
    </DialogScrollbar>
  );
};
